import { useState, useEffect, useRef, useCallback } from "react";
import {
  getNotNegativeNumber,
  getString,
  isArrayEmpty,
  isPositiveInt,
} from "../utils/handler_validations";
import { cancellationDB } from "../services";
import { useSessionContext } from "../context";
import { useVeryNotificationsContext } from "../components/common/v2/VeryNotifications/useVeryNotifications";

const getLoadErrorTemplate = (initialLoading = false) => ({
  loading: initialLoading === true,
  error: { message: "", details: "" },
});

const PAGE_LIMIT = 50;

const getRowTemplate = () => ({
  serviceId: 0,
  canceledBy: 0,
  motive: "",
  canceledDate: null,
  cost: 0,
  distance: 0,
  passengerId: 0,
  driverId: 0,
  passengerName: "",
  passengerLastName: "",
  passengerProfileImg: "",
  driverName: "",
  driverLastName: "",
  driverProfileImg: "",
  status: "PENDING",
  driverBlocked: false,
  passengerCompensated: false,
});

export const useCancellations = () => {
  const session = useSessionContext();
  const notis = useVeryNotificationsContext();

  const [page, setPage] = useState(getLoadErrorTemplate(true));
  const [list, setList] = useState([getRowTemplate()]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const offsetRef = useRef(0);

  const [filterCanceledBy, setFilterCanceledBy] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const filterSearchRef = useRef("");

  const [selectedServiceId, setSelectedServiceId] = useState(0);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const [modalBlock, setModalBlock] = useState({
    show: false,
    driverId: 0,
    driverName: "",
  });
  const [modalCompensate, setModalCompensate] = useState({
    show: false,
    passengerId: 0,
    passengerName: "",
  });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    getListInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFormattedList = (rawList = [], initial = false) => {
    if (isArrayEmpty(rawList)) return initial ? undefined : [];
    return rawList.map((row) => ({
      serviceId: getNotNegativeNumber(row.serviceId),
      canceledBy: getNotNegativeNumber(row.canceledBy),
      motive: getString(row.motive),
      canceledDate: row.canceledDate ?? null,
      cost: getNotNegativeNumber(row.cost),
      distance: getNotNegativeNumber(row.distance),
      passengerId: getNotNegativeNumber(row.passengerId),
      driverId: getNotNegativeNumber(row.driverId),
      passengerName: getString(row.passengerName),
      passengerLastName: getString(row.passengerLastName),
      passengerProfileImg: getString(row.passengerProfileImg),
      driverName: getString(row.driverName),
      driverLastName: getString(row.driverLastName),
      driverProfileImg: getString(row.driverProfileImg),
      status: getString(row.status),
      driverBlocked: row.driverBlocked === true,
      passengerCompensated: row.passengerCompensated === true,
    }));
  };

  const getListInitial = async () => {
    setPage(getLoadErrorTemplate(true));
    setSelectedServiceId(0);
    setDetail(null);
    offsetRef.current = 0;
    filterSearchRef.current = "";
    const resp = await cancellationDB.getListCancellations(
      { offset: 0, filterCanceledBy, filterSearch },
      session.value.jwt,
    );
    if (resp.error) {
      setPage({
        loading: false,
        error: { message: resp.error.message, details: resp.error.details },
      });
      return;
    }
    const formatted = getFormattedList(resp.body.list, true);
    offsetRef.current = formatted?.length ?? 0;
    setList(formatted);
    setTotal(getNotNegativeNumber(resp.body.total));
    setHasMore(
      (formatted?.length ?? 0) < getNotNegativeNumber(resp.body.total),
    );
    setPage(getLoadErrorTemplate(false));
  };

  const getListFiltered = async (overrideCanceledBy) => {
    const canceledByVal =
      overrideCanceledBy !== undefined ? overrideCanceledBy : filterCanceledBy;
    setPage(getLoadErrorTemplate(true));
    setSelectedServiceId(0);
    setDetail(null);
    offsetRef.current = 0;
    const resp = await cancellationDB.getListCancellations(
      { offset: 0, filterCanceledBy: canceledByVal, filterSearch },
      session.value.jwt,
    );
    if (resp.error) {
      setPage({
        loading: false,
        error: { message: resp.error.message, details: resp.error.details },
      });
      return;
    }
    const formatted = getFormattedList(resp.body.list, false);
    offsetRef.current = formatted?.length ?? 0;
    setList(formatted);
    setTotal(getNotNegativeNumber(resp.body.total));
    setHasMore(
      (formatted?.length ?? 0) < getNotNegativeNumber(resp.body.total),
    );
    setPage(getLoadErrorTemplate(false));
  };

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const resp = await cancellationDB.getListCancellations(
      { offset: offsetRef.current, filterCanceledBy, filterSearch },
      session.value.jwt,
    );
    if (resp.error) {
      notis.createNotiError(
        "Error al cargar más cancelaciones",
        resp.error.details,
      );
      setLoadingMore(false);
      return;
    }
    const more = getFormattedList(resp.body.list, false);
    const newList = [...(list ?? []), ...more];
    offsetRef.current = newList.length;
    setList(newList);
    setTotal(getNotNegativeNumber(resp.body.total));
    setHasMore(newList.length < getNotNegativeNumber(resp.body.total));
    setLoadingMore(false);
  };

  const loadDetail = useCallback(
    async (serviceId = 0) => {
      if (!isPositiveInt(serviceId)) return;
      setSelectedServiceId(serviceId);
      setDetailLoading(true);
      setDetailError("");
      setDetail(null);
      const resp = await cancellationDB.getCancellationDetail(
        serviceId,
        session.value.jwt,
      );
      if (resp.error) {
        setDetailError(
          resp.error.message || "No fue posible obtener el detalle",
        );
        setDetailLoading(false);
        return;
      }
      setDetail(resp.body);
      setDetailLoading(false);
    },
    [session.value.jwt],
  );

  const closeDetail = () => {
    setSelectedServiceId(0);
    setDetail(null);
    setDetailError("");
  };

  /*───────────────────────────────────────────────────────────
    MODAL BLOCK DRIVER
  ───────────────────────────────────────────────────────────*/
  const showModalBlockDriver = (driverId = 0, driverName = "") => {
    setModalBlock({ show: true, driverId, driverName });
  };
  const closeModalBlockDriver = () =>
    setModalBlock({ show: false, driverId: 0, driverName: "" });

  const execBlockDriver = async ({
    blockDuration = "",
    blockReason = "",
  } = {}) => {
    if (!isPositiveInt(modalBlock.driverId)) return;
    setActionLoading(true);
    const resp = await cancellationDB.blockDriver(
      {
        serviceId: selectedServiceId,
        driverId: modalBlock.driverId,
        blockDuration,
        blockReason,
      },
      session.value.jwt,
    );
    setActionLoading(false);
    if (resp.error) {
      notis.createNotiError(
        "No fue posible bloquear al conductor",
        resp.error.details,
      );
      return;
    }
    notis.createNotiSuccess(
      `Conductor bloqueado hasta ${new Date(resp.body.blockUntil).toLocaleString("es-MX")}`,
    );
    closeModalBlockDriver();
    if (detail) {
      setDetail((prev) => ({
        ...prev,
        trip: {
          ...prev.trip,
          driverBlocked: true,
          status: prev.trip?.passengerCompensated ? "COMPLETED" : "IN_PROGRESS",
        },
        driver: {
          ...prev.driver,
          activeBlock: {
            id: resp.body.blockId,
            blockUntil: resp.body.blockUntil,
            reason: blockReason,
          },
        },
      }));
    }
    setList((prev) =>
      (prev ?? []).map((row) =>
        row.serviceId === selectedServiceId
          ? {
              ...row,
              driverBlocked: true,
              status: row.passengerCompensated ? "COMPLETED" : "IN_PROGRESS",
            }
          : row,
      ),
    );
  };

  /*───────────────────────────────────────────────────────────
    MODAL COMPENSATE PASSENGER
  ───────────────────────────────────────────────────────────*/
  const showModalCompensatePassenger = (
    passengerId = 0,
    passengerName = "",
  ) => {
    setModalCompensate({ show: true, passengerId, passengerName });
  };
  const closeModalCompensatePassenger = () =>
    setModalCompensate({ show: false, passengerId: 0, passengerName: "" });

  const execCompensatePassenger = async ({
    couponAmount = 0,
    couponExpDate = "",
  } = {}) => {
    if (!isPositiveInt(modalCompensate.passengerId)) return;
    setActionLoading(true);
    const resp = await cancellationDB.compensatePassenger(
      {
        serviceId: selectedServiceId,
        passengerId: modalCompensate.passengerId,
        couponAmount,
        couponExpDate,
      },
      session.value.jwt,
    );
    setActionLoading(false);
    if (resp.error) {
      notis.createNotiError(
        "No fue posible compensar al pasajero",
        resp.error.details,
      );
      return;
    }
    notis.createNotiSuccess(
      `Cupón $${couponAmount} asignado a cartera del pasajero`,
    );
    closeModalCompensatePassenger();
    if (detail) {
      setDetail((prev) => ({
        ...prev,
        passenger: {
          ...prev.passenger,
          compensation: {
            amount: couponAmount,
            couponCode: resp.body.coupon?.code ?? "",
            date: new Date().toISOString(),
          },
        },
      }));
    }
    // update list: mark this service as compensated
    setList((prev) =>
      (prev ?? []).map((row) =>
        row.serviceId === selectedServiceId
          ? {
              ...row,
              passengerCompensated: true,
              status: row.driverBlocked ? "COMPLETED" : "IN_PROGRESS",
            }
          : row,
      ),
    );
    if (detail) {
      setDetail((prev) => ({
        ...prev,
        trip: {
          ...prev.trip,
          passengerCompensated: true,
          status: prev.trip?.driverBlocked ? "COMPLETED" : "IN_PROGRESS",
        },
        passenger: {
          ...prev.passenger,
          compensation: {
            amount: couponAmount,
            couponCode: resp.body.coupon?.code ?? "",
            date: new Date().toISOString(),
          },
        },
      }));
    }
  };

  /*───────────────────────────────────────────────────────────
    MARK COMPLETED
  ───────────────────────────────────────────────────────────*/
  const execMarkCompleted = async () => {
    if (!isPositiveInt(selectedServiceId)) return;
    setActionLoading(true);
    const resp = await cancellationDB.markCompleted(
      { serviceId: selectedServiceId },
      session.value.jwt,
    );
    setActionLoading(false);
    if (resp.error) {
      notis.createNotiError(
        "No fue posible marcar como completado",
        resp.error.details,
      );
      return;
    }
    notis.createNotiSuccess("Caso marcado como completado");
    setList((prev) =>
      (prev ?? []).map((row) =>
        row.serviceId === selectedServiceId
          ? { ...row, status: "COMPLETED" }
          : row,
      ),
    );
    if (detail) {
      setDetail((prev) => ({
        ...prev,
        trip: {
          ...prev.trip,
          status: "COMPLETED",
        },
      }));
    }
  };

  const onChangeFilterCanceledBy = (val = "") => setFilterCanceledBy(val);
  const onChangeFilterSearch = (val = "") => setFilterSearch(val);

  const getEmptyMsg = () => {
    if (list === undefined)
      return {
        text: "No hay cancelaciones registradas",
        icon: { name: "content_paste", type: "symbols_outlined" },
      };
    if (isArrayEmpty(list))
      return {
        text: "Ninguna cancelación coincide con la búsqueda",
        icon: { name: "content_paste_search", type: "symbols_outlined" },
      };
    return { text: "", icon: { name: "", type: "" } };
  };

  return {
    page,
    list,
    total,
    hasMore,
    loadingMore,
    loadMore,
    filterCanceledBy,
    filterSearch,
    selectedServiceId,
    detail,
    detailLoading,
    detailError,
    modalBlock,
    modalCompensate,
    actionLoading,
    getListInitial,
    getListFiltered,
    loadDetail,
    closeDetail,
    showModalBlockDriver,
    closeModalBlockDriver,
    execBlockDriver,
    showModalCompensatePassenger,
    closeModalCompensatePassenger,
    execCompensatePassenger,
    execMarkCompleted,
    onChangeFilterCanceledBy,
    onChangeFilterSearch,
    getEmptyMsg,
    PAGE_LIMIT,
  };
};
